import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import type { PecaCorte, ConfiguracaoChapa, EixoCodigo, GrupoCorte, DadosProjeto, ProjetoSalvo, ItemProducao, PecaAcabada } from '../../types/corte';
import {
  contarProjetosCatalogo,
  gravarLegadoCatalogoLocalStorage,
  lerLegadoCatalogoLocalStorage,
  mesclarEstadoCatalogo,
  chaveParProjetoCatalogo,
  filtrarProjetosCatalogoNaoExcluidos,
  liberarExclusaoProjetoCatalogo,
  registrarExclusaoProjetoCatalogo,
  normalizarJsonCatalogoParaGravacao,
} from '../../utils/adePersistenciaCatalogo';
import { projetosDoBackupEmbutido, catalogoMigradoParaZustand, carregarCatalogoMigrado, mesclarProjetosCatalogo } from '../../utils/recuperarCatalogoMigrado';
import { notificarSyncDesktop } from '../../utils/adeDesktopSync';

const STORE_CORTE_PERSIST_KEY = 'planos-corte-ade';
const DB_CORTE_PERSIST = 'ade-tempo-corte-db';
const DB_CORTE_PERSIST_VERSION = 9;
const STORE_CORTE_PERSIST = 'zustandPersist';

const abrirDbCortePersist = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_CORTE_PERSIST, DB_CORTE_PERSIST_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_CORTE_PERSIST)) {
        db.createObjectStore(STORE_CORTE_PERSIST);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const dbGetTexto = async (key: string): Promise<string | null> => {
  try {
    const db = await abrirDbCortePersist();
    const valor = await new Promise<string | null>((resolve, reject) => {
      const tx = db.transaction(STORE_CORTE_PERSIST, 'readonly');
      const req = tx.objectStore(STORE_CORTE_PERSIST).get(key);
      req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return valor;
  } catch {
    return null;
  }
};

let notifyCatalogoTimer: ReturnType<typeof setTimeout> | null = null;
let ultimoFingerprintCatalogo = '';

/** Só notifica outras janelas quando a lista de projetos do catálogo muda (não config global). */
function fingerprintProjetosSalvosJson(json: string): string {
  try {
    const p = JSON.parse(json) as { state?: { projetosSalvos?: unknown[] }; projetosSalvos?: unknown[] };
    const list = Array.isArray(p?.state?.projetosSalvos)
      ? p.state!.projetosSalvos
      : Array.isArray(p?.projetosSalvos)
        ? p.projetosSalvos
        : [];
    return `${list.length}|${list
      .map((x) => (x && typeof x === 'object' ? String((x as { id?: string }).id || '') : ''))
      .join(',')}`;
  } catch {
    return '';
  }
}

function agendarNotificarCatalogo(json: string): void {
  const fp = fingerprintProjetosSalvosJson(json);
  if (!fp || fp === ultimoFingerprintCatalogo) return;
  ultimoFingerprintCatalogo = fp;
  if (notifyCatalogoTimer) clearTimeout(notifyCatalogoTimer);
  notifyCatalogoTimer = setTimeout(() => {
    notificarSyncDesktop('catalogo');
  }, 700);
}

const dbSetTexto = async (
  key: string,
  value: string,
  opts?: { silent?: boolean }
): Promise<void> => {
  const db = await abrirDbCortePersist();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CORTE_PERSIST, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE_CORTE_PERSIST).put(value, key);
  });
  db.close();
  if (key === STORE_CORTE_PERSIST_KEY && !opts?.silent) {
    agendarNotificarCatalogo(value);
  }
};

const dbRemoverTexto = async (key: string): Promise<void> => {
  const db = await abrirDbCortePersist();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CORTE_PERSIST, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE_CORTE_PERSIST).delete(key);
  });
  db.close();
};

let persistDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let persistDebouncePending: { name: string; value: string } | null = null;

export async function flushPersistenciaCatalogo(): Promise<void> {
  if (persistDebounceTimer) {
    clearTimeout(persistDebounceTimer);
    persistDebounceTimer = null;
  }
  const job = persistDebouncePending;
  persistDebouncePending = null;
  if (job) await gravarPersistCorte(job.name, job.value);
}

const gravarPersistCorte = async (name: string, value: string): Promise<void> => {
  const qtdNovo = contarProjetosCatalogo(value);
  const dbVal = await dbGetTexto(name);
  const legado = lerLegadoCatalogoLocalStorage(name);
  const maxExistente = Math.max(contarProjetosCatalogo(dbVal), contarProjetosCatalogo(legado));

  if (qtdNovo === 0 && maxExistente > 0) {
    return;
  }

  const final = normalizarJsonCatalogoParaGravacao(value);
  gravarLegadoCatalogoLocalStorage(name, final);
  await dbSetTexto(name, final);
};

const cortePersistStorage: StateStorage = {
  getItem: async (name) => {
    const dbVal = await dbGetTexto(name);
    const legado = lerLegadoCatalogoLocalStorage(name);

    const mesclado = mesclarEstadoCatalogo(dbVal, legado);
    if (!mesclado) return null;

    const qtdMesclado = contarProjetosCatalogo(mesclado);
    const qtdDb = contarProjetosCatalogo(dbVal);
    const qtdLegado = contarProjetosCatalogo(legado);

    if (qtdMesclado > qtdDb || qtdMesclado > qtdLegado) {
      gravarLegadoCatalogoLocalStorage(name, mesclado);
      await dbSetTexto(name, mesclado, { silent: true });
    }

    return mesclado;
  },
  setItem: async (name, value) => {
    const normalizado = normalizarJsonCatalogoParaGravacao(value);
    gravarLegadoCatalogoLocalStorage(name, normalizado);
    persistDebouncePending = { name, value: normalizado };
    if (persistDebounceTimer) clearTimeout(persistDebounceTimer);
    persistDebounceTimer = setTimeout(() => {
      const job = persistDebouncePending;
      persistDebouncePending = null;
      persistDebounceTimer = null;
      if (!job) return;
      void gravarPersistCorte(job.name, job.value);
    }, 450);
  },
  removeItem: async (name) => {
    await dbRemoverTexto(name);
    try {
      localStorage.removeItem(name);
    } catch {
      /* ignore */
    }
  },
};

interface CorteStore {
  // Dados do projeto
  projeto: DadosProjeto;
  setProjeto: (projeto: Partial<DadosProjeto>) => void;
  
  // Configuração da chapa
  configuracao: ConfiguracaoChapa;
  setConfiguracao: (config: Partial<ConfiguracaoChapa>) => void;
  
  // Rotação do desenho
  rotacaoDesenho: number;
  girarDesenho: () => void;
  
  // Peças lançadas
  pecas: PecaCorte[];
  grupoAtualId: string;
  adicionarPeca: (peca: Omit<PecaCorte, 'id' | 'grupoId'>) => void;
  atualizarPeca: (id: string, patch: { medida?: number; quantidade?: number }) => void;
  removerPeca: (id: string) => void;
  limparPecas: () => void;
  restaurarPecas: (pecas: Array<Partial<PecaCorte> & Pick<PecaCorte, 'codigo' | 'medida' | 'quantidade'>>) => void;
  novoGrupo: () => void;
  
  // Projetos salvos (catálogo)
  projetosSalvos: ProjetoSalvo[];
  salvarProjeto: () => void;
  carregarProjeto: (id: string) => void;
  removerProjetoSalvo: (id: string) => void;
  recuperarCatalogoEmergencia: () => Promise<number>;
  /** Restaura catálogo do backup embutido / disco (emergência NW.js). */
  recuperarCatalogoEmergencia: () => Promise<number>;

  // Material/espessura do plano (para salvar junto do projeto)
  materialConfig: { tipoMaterial: 'aglomerado' | 'mdf'; espessuraMaterial: number };
  setMaterialConfig: (cfg: Partial<CorteStore['materialConfig']>) => void;
  
  // Lista de produção
  itensProducao: ItemProducao[];
  adicionarItemProducao: (item: Omit<ItemProducao, 'id' | 'quantidadePorChapa'>) => void;
  removerItemProducao: (id: string) => void;
  limparProducao: () => void;
  calcularChapasNecessarias: () => { chapas: number; detalhes: string };
  
  // Calcular peças acabadas
  calcularPecasAcabadas: () => PecaAcabada[];
  
  // Cálculos
  calcularGrupos: () => GrupoCorte[];
  calcularConsumo: () => {
    totalLargura: number;
    totalComprimento: number;
    sobraLargura: number;
    sobraComprimento: number;
    grupos: GrupoCorte[];
    pecasAcabadas: PecaAcabada[];
  };
}

type BlocoSequencial = {
  medidaLongitudinal: number;
  qtdLongitudinal: number;
  medidaTransversal: number;
  qtdTransversal: number;
  /** Código 5: subdivide a peça dentro da MESMA tira (não cria nova faixa longitudinal) */
  subLongMed?: number;
  subLongQtd?: number;
};

type TiraSequencial = {
  medidaLongitudinal: number;
  qtdLongitudinal: number;
  transversais: Array<{ medida: number; qtd: number; subLongMed?: number; subLongQtd?: number }>;
};

function extrairBlocosSequenciais(pecas: PecaCorte[]): BlocoSequencial[] {
  const blocos: BlocoSequencial[] = [];
  let longAtualMedida = 0;
  let longAtualQtd = 1;
  let transRefMedida = 0;
  let transRefQtd = 1;
  let restanteLong5 = 0;
  const serraCorte5 = 5;

  for (const p of pecas) {
    if (p.codigo === 3) {
      longAtualMedida = Number(p.medida) || 0;
      longAtualQtd = Math.max(1, Number(p.quantidade) || 1);
      continue;
    }

    if (p.codigo === 4 || p.codigo === 44) {
      if (longAtualMedida <= 0) continue;
      const blocoBase: BlocoSequencial = {
        medidaLongitudinal: longAtualMedida,
        qtdLongitudinal: longAtualQtd,
        medidaTransversal: Number(p.medida) || 0,
        qtdTransversal: Math.max(1, Number(p.quantidade) || 1),
      };
      blocos.push(blocoBase);
      transRefMedida = blocoBase.medidaTransversal;
      transRefQtd = blocoBase.qtdTransversal;
      restanteLong5 = longAtualMedida;
      continue;
    }

    if (p.codigo === 5) {
      // Regra: múltiplos código 5 são encadeados na sobra do corte anterior.
      if (blocos.length === 0 || transRefMedida <= 0) continue;
      const baseLong = restanteLong5 > 0 ? restanteLong5 : longAtualMedida;
      if (baseLong <= 0) continue;
      const qtd5 = Math.max(1, Number(p.quantidade) || 1);
      const corteInformado = Number(p.medida) || baseLong;
      const corteEfetivo = Math.min(corteInformado, baseLong);
      blocos.push({
        medidaLongitudinal: longAtualMedida,
        qtdLongitudinal: longAtualQtd,
        medidaTransversal: transRefMedida,
        qtdTransversal: transRefQtd,
        subLongMed: corteEfetivo,
        subLongQtd: qtd5,
      });
      restanteLong5 = Math.max(0, baseLong - (corteEfetivo + serraCorte5) * qtd5);
    }
  }

  return blocos;
}

function agruparEmTiras(blocos: BlocoSequencial[]): TiraSequencial[] {
  const tiras: TiraSequencial[] = [];
  blocos.forEach((b) => {
    const last = tiras[tiras.length - 1];
    const podeMesclar =
      !!last &&
      last.medidaLongitudinal === b.medidaLongitudinal &&
      last.qtdLongitudinal === b.qtdLongitudinal;

    const transEntry = {
      medida: b.medidaTransversal,
      qtd: b.qtdTransversal,
      ...(b.subLongMed != null && { subLongMed: b.subLongMed, subLongQtd: b.subLongQtd ?? 1 }),
    };
    if (podeMesclar) {
      last.transversais.push(transEntry);
    } else {
      tiras.push({
        medidaLongitudinal: b.medidaLongitudinal,
        qtdLongitudinal: b.qtdLongitudinal,
        transversais: [transEntry],
      });
    }
  });
  return tiras;
}

function extrairTirasConsumo(pecas: PecaCorte[]): Array<{ longMed: number; longQtd: number; transversais: Array<{ med: number; qtd: number }> }> {
  const tiras: Array<{ longMed: number; longQtd: number; transversais: Array<{ med: number; qtd: number }> }> = [];
  let atual: { longMed: number; longQtd: number; transversais: Array<{ med: number; qtd: number }> } | null = null;

  (pecas || []).forEach((p) => {
    if (p.codigo === 3) {
      atual = {
        longMed: Number(p.medida) || 0,
        longQtd: Math.max(1, Number(p.quantidade) || 1),
        transversais: [],
      };
      tiras.push(atual);
      return;
    }
    if (p.codigo === 4 || p.codigo === 44) {
      if (!atual) return;
      atual.transversais.push({
        med: Number(p.medida) || 0,
        qtd: Math.max(1, Number(p.quantidade) || 1),
      });
      return;
    }
    // código 5 não altera consumo da chapa; apenas peça final
  });

  return tiras;
}

/** Grupos de corte a partir de peças salvas (catálogo, pré-visualização). */
export function calcularGruposFromPecas(
  pecas: PecaCorte[],
  configuracao: ConfiguracaoChapa
): GrupoCorte[] {
  const larguraTotal = Number(configuracao?.larguraTotal) || 2200;
  const comprimentoTotal = Number(configuracao?.comprimentoTotal) || 2750;
  const espessuraSerra = Number(configuracao?.espessuraSerra) || 4;
  const cfg = { larguraTotal, comprimentoTotal, espessuraSerra };
  const gruposMap = new Map<string, GrupoCorte>();

  pecas.forEach((peca) => {
    if (!gruposMap.has(peca.grupoId)) {
      gruposMap.set(peca.grupoId, {
        id: peca.grupoId,
        larguraDisponivel: cfg.larguraTotal,
        comprimentoDisponivel: cfg.comprimentoTotal,
        consumoLargura: 0,
        consumoComprimento: 0,
        pecas: [],
      });
    }
  });

  pecas.forEach((peca) => {
    const grupo = gruposMap.get(peca.grupoId);
    if (grupo) grupo.pecas.push(peca);
  });

  gruposMap.forEach((grupo) => {
    const tiras = extrairTirasConsumo(grupo.pecas);
    grupo.consumoLargura = tiras.reduce((sum, t) => sum + (t.longMed + cfg.espessuraSerra) * t.longQtd, 0);
    grupo.consumoComprimento = tiras.reduce((mx, t) => {
      const comp = t.transversais.reduce((s, tr) => s + (tr.med + cfg.espessuraSerra) * tr.qtd, 0);
      return Math.max(mx, comp);
    }, 0);
  });

  gruposMap.forEach((grupo) => {
    grupo.larguraDisponivel = cfg.larguraTotal - grupo.consumoLargura;
    grupo.comprimentoDisponivel = cfg.comprimentoTotal - grupo.consumoComprimento;
  });

  return Array.from(gruposMap.values());
}

export function calcularPecasAcabadasFromGrupos(grupos: GrupoCorte[]): PecaAcabada[] {
  const pecasAcabadas: PecaAcabada[] = [];
  grupos.forEach((grupo) => {
    const blocos = extrairBlocosSequenciais(grupo.pecas);
    blocos.forEach((b) => {
      if (b.subLongMed != null) {
        const qtdTotal = b.qtdLongitudinal * b.qtdTransversal * (b.subLongQtd ?? 1);
        pecasAcabadas.push({
          medidaLongitudinal: b.subLongMed,
          medidaTransversal: b.medidaTransversal,
          quantidade: qtdTotal,
        });
      } else {
        const qtdTotal = b.qtdLongitudinal * b.qtdTransversal;
        pecasAcabadas.push({
          medidaLongitudinal: b.medidaLongitudinal,
          medidaTransversal: b.medidaTransversal,
          quantidade: qtdTotal,
        });
      }
    });
  });
  return pecasAcabadas;
}

export const useCorteStore = create<CorteStore>()(
  persist(
    (set, get) => ({
      projeto: {
        nomePeca: '',
        nomeProduto: '',
        dataCriacao: new Date().toLocaleDateString('pt-BR'),
      },
      
      configuracao: {
        larguraTotal: 2200,
        comprimentoTotal: 2750,
        espessuraSerra: 4,
      },
      
      rotacaoDesenho: 0,
      
      pecas: [],
      grupoAtualId: crypto.randomUUID(),
      
      projetosSalvos: [],

      materialConfig: { tipoMaterial: 'aglomerado', espessuraMaterial: 15 },
      
      itensProducao: [],
      
      setProjeto: (projeto) => set((state) => ({
        projeto: { ...state.projeto, ...projeto }
      })),
      
      setConfiguracao: (config) => set((state) => ({
        configuracao: { ...state.configuracao, ...config }
      })),

      setMaterialConfig: (cfg) => set((state) => ({
        materialConfig: { ...state.materialConfig, ...cfg }
      })),
      
      girarDesenho: () => set((state) => ({
        rotacaoDesenho: (state.rotacaoDesenho + 90) % 360
      })),
      
      novoGrupo: () => set({ grupoAtualId: crypto.randomUUID() }),
      
      adicionarPeca: (peca) => set((state) => {
        const novoGrupoId = peca.codigo === 3 ? crypto.randomUUID() : state.grupoAtualId;
        return {
          pecas: [...state.pecas, { ...peca, id: crypto.randomUUID(), grupoId: novoGrupoId }],
          grupoAtualId: novoGrupoId
        };
      }),
      
      atualizarPeca: (id, patch) => set((state) => ({
        pecas: state.pecas.map((p) => {
          if (p.id !== id) return p;
          return {
            ...p,
            ...(patch.medida !== undefined ? { medida: Number(patch.medida) || 0 } : {}),
            ...(patch.quantidade !== undefined
              ? { quantidade: Math.max(1, Number(patch.quantidade) || 1) }
              : {}),
          };
        }),
      })),

      removerPeca: (id) => set((state) => ({
        pecas: state.pecas.filter(p => p.id !== id)
      })),
      
      limparPecas: () => set({ pecas: [], grupoAtualId: crypto.randomUUID() }),
      
      restaurarPecas: (pecasOriginais) => {
        if (!pecasOriginais || pecasOriginais.length === 0) {
          set({ pecas: [], grupoAtualId: crypto.randomUUID() });
          return;
        }
        const mapaGrupo = new Map<string, string>();
        let grupoAtual = crypto.randomUUID();
        const novasPecas: PecaCorte[] = pecasOriginais.map((p) => {
          const grupoIdAntigo = (p as PecaCorte).grupoId;
          let novoGrupoId: string;
          if (grupoIdAntigo) {
            novoGrupoId = mapaGrupo.get(grupoIdAntigo) ?? (() => {
              const novo = crypto.randomUUID();
              mapaGrupo.set(grupoIdAntigo, novo);
              return novo;
            })();
          } else {
            novoGrupoId = p.codigo === 3 ? (grupoAtual = crypto.randomUUID()) : grupoAtual;
          }
          return {
            id: crypto.randomUUID(),
            codigo: p.codigo,
            medida: p.medida,
            quantidade: p.quantidade,
            grupoId: novoGrupoId,
          };
        });
        set({ pecas: novasPecas, grupoAtualId: novasPecas[novasPecas.length - 1]?.grupoId ?? crypto.randomUUID() });
      },
      
      salvarProjeto: () => {
        const state = get();
        if (state.pecas.length === 0 || !state.projeto.nomeProduto) return;

        const par = chaveParProjetoCatalogo(state.projeto.nomeProduto, state.projeto.nomePeca);
        const existente = state.projetosSalvos.find(
          (p) => chaveParProjetoCatalogo(p.nomeProduto, p.nomePeca) === par
        );

        const novoProjeto: ProjetoSalvo = {
          id: existente?.id ?? crypto.randomUUID(),
          nomeProduto: state.projeto.nomeProduto,
          nomePeca: state.projeto.nomePeca,
          pecas: [...state.pecas],
          configuracao: { ...state.configuracao },
          tipoMaterial: state.materialConfig.tipoMaterial,
          espessuraMaterial: state.materialConfig.espessuraMaterial,
          dataCriacao: existente?.dataCriacao ?? new Date().toLocaleDateString('pt-BR'),
        };

        liberarExclusaoProjetoCatalogo(
          novoProjeto.nomeProduto,
          novoProjeto.nomePeca,
          novoProjeto.id
        );

        const outros = state.projetosSalvos.filter(
          (p) => chaveParProjetoCatalogo(p.nomeProduto, p.nomePeca) !== par
        );

        set({
          projetosSalvos: [...outros, novoProjeto].sort((a, b) =>
            a.nomeProduto.localeCompare(b.nomeProduto)
          ),
        });
      },
      
      carregarProjeto: (id: string) => {
        const state = get();
        const projeto = state.projetosSalvos.find(p => p.id === id);
        if (!projeto) return;
        
        set({
          pecas: projeto.pecas.map(p => ({ ...p, id: crypto.randomUUID() })),
          configuracao: { ...projeto.configuracao },
          materialConfig: {
            tipoMaterial: (projeto.tipoMaterial ?? 'aglomerado'),
            espessuraMaterial: Number(projeto.espessuraMaterial ?? 15) || 15,
          },
          projeto: { nomeProduto: projeto.nomeProduto, nomePeca: projeto.nomePeca, dataCriacao: projeto.dataCriacao },
          grupoAtualId: crypto.randomUUID()
        });
      },
      
      removerProjetoSalvo: (id) =>
        set((state) => {
          const removido = state.projetosSalvos.find((p) => p.id === id);
          if (removido) registrarExclusaoProjetoCatalogo(removido);
          return {
            projetosSalvos: state.projetosSalvos.filter((p) => p.id !== id),
          };
        }),

      recuperarCatalogoEmergencia: async () => {
        const backup = await carregarCatalogoMigrado();
        if (!backup.length) return 0;
        const mesclados = mesclarProjetosCatalogo(get().projetosSalvos, backup);
        set({ projetosSalvos: mesclados });
        const json = catalogoMigradoParaZustand(mesclados);
        await dbSetTexto(STORE_CORTE_PERSIST_KEY, json);
        gravarLegadoCatalogoLocalStorage(STORE_CORTE_PERSIST_KEY, json);
        return mesclados.length;
      },
      
      calcularPecasAcabadas: () => calcularPecasAcabadasFromGrupos(get().calcularGrupos()),
      
      adicionarItemProducao: (item) => set((state) => {
        // Calcular quantas peças dessa saem por chapa baseado no plano atual
        const grupos = get().calcularGrupos();
        let quantidadePorChapa = 1;
        
        grupos.forEach(grupo => {
          const blocos = extrairBlocosSequenciais(grupo.pecas);
          blocos.forEach((b) => {
            const long = b.subLongMed ?? b.medidaLongitudinal;
            const trans = b.medidaTransversal;
            const qtd = b.subLongMed != null
              ? b.qtdLongitudinal * b.qtdTransversal * (b.subLongQtd ?? 1)
              : b.qtdLongitudinal * b.qtdTransversal;
            if (long === item.medidaLongitudinal && trans === item.medidaTransversal) {
              quantidadePorChapa = qtd;
            }
          });
        });
        
        return {
          itensProducao: [...state.itensProducao, { ...item, id: crypto.randomUUID(), quantidadePorChapa }]
        };
      }),
      
      removerItemProducao: (id) => set((state) => ({
        itensProducao: state.itensProducao.filter(i => i.id !== id)
      })),
      
      limparProducao: () => set({ itensProducao: [] }),
      
      calcularChapasNecessarias: () => {
        const { itensProducao } = get();
        
        if (itensProducao.length === 0) {
          return { chapas: 0, detalhes: 'Nenhum item na lista de produção' };
        }
        
        let maxChapas = 1;
        const detalhesItens: string[] = [];
        
        itensProducao.forEach(item => {
          if (item.quantidadePorChapa > 0) {
            const chapasNecessarias = Math.ceil(item.quantidadeNecessaria / item.quantidadePorChapa);
            maxChapas = Math.max(maxChapas, chapasNecessarias);
            detalhesItens.push(`${item.medidaLongitudinal}x${item.medidaTransversal}: ${item.quantidadeNecessaria} peças ÷ ${item.quantidadePorChapa}/chapa = ${chapasNecessarias} chapas`);
          }
        });
        
        return { 
          chapas: maxChapas, 
          detalhes: detalhesItens.join('\n')
        };
      },
      
      calcularGrupos: () => calcularGruposFromPecas(get().pecas, get().configuracao),
      
      calcularConsumo: () => {
        const { configuracao } = get();
        const grupos = get().calcularGrupos();
        const pecasAcabadas = get().calcularPecasAcabadas();
        
        const totalLargura = grupos.reduce((sum, g) => sum + g.consumoLargura, 0);
        const maxConsumoComprimento = Math.max(...grupos.map(g => g.consumoComprimento), 0);
        
        return {
          totalLargura,
          totalComprimento: maxConsumoComprimento,
          sobraLargura: configuracao.larguraTotal - totalLargura,
          sobraComprimento: configuracao.comprimentoTotal - maxConsumoComprimento,
          grupos,
          pecasAcabadas
        };
      },
    }),
    {
      name: STORE_CORTE_PERSIST_KEY,
      storage: createJSONStorage(() => cortePersistStorage),
      /** Evita gravar [] no IDB antes do getItem terminar (janelas filhas NW.js). */
      skipHydration: true,
      partialize: (state) => ({
        projetosSalvos: state.projetosSalvos,
        configuracao: state.configuracao,
        materialConfig: state.materialConfig,
      }),
      merge: (persistedState, currentState) => {
        const p = persistedState as {
          projetosSalvos?: unknown;
          configuracao?: { larguraTotal?: number; comprimentoTotal?: number; espessuraSerra?: number };
          materialConfig?: { tipoMaterial?: 'aglomerado' | 'mdf'; espessuraMaterial?: number };
        };
        const def = { larguraTotal: 2200, comprimentoTotal: 2750, espessuraSerra: 4 };
        const config = p?.configuracao && typeof p.configuracao === 'object'
          ? {
              larguraTotal: Number(p.configuracao.larguraTotal) || def.larguraTotal,
              comprimentoTotal: Number(p.configuracao.comprimentoTotal) || def.comprimentoTotal,
              espessuraSerra: Number(p.configuracao.espessuraSerra) || def.espessuraSerra,
            }
          : def;
        return {
          ...currentState,
          projetosSalvos: Array.isArray(p?.projetosSalvos) ? p.projetosSalvos : currentState.projetosSalvos,
          configuracao: config,
          materialConfig: {
            tipoMaterial: (p?.materialConfig?.tipoMaterial ?? currentState.materialConfig.tipoMaterial),
            espessuraMaterial: Number(p?.materialConfig?.espessuraMaterial ?? currentState.materialConfig.espessuraMaterial) || currentState.materialConfig.espessuraMaterial,
          },
        };
      },
    }
  )
);

/** Sync entre janelas: disco prevalece (sem mesclar cópias que ressuscitam apagados). */
export async function sincronizarApenasProjetosSalvosDoDisco(): Promise<void> {
  const raw = await cortePersistStorage.getItem(STORE_CORTE_PERSIST_KEY);
  if (!raw) return;
  try {
    const parsed = JSON.parse(raw) as {
      state?: { projetosSalvos?: ProjetoSalvo[] };
      projetosSalvos?: ProjetoSalvo[];
    };
    const remotos = Array.isArray(parsed?.state?.projetosSalvos)
      ? parsed.state!.projetosSalvos!
      : Array.isArray(parsed?.projetosSalvos)
        ? parsed.projetosSalvos
        : [];
    useCorteStore.setState({
      projetosSalvos: filtrarProjetosCatalogoNaoExcluidos(
        mesclarProjetosCatalogo(remotos)
      ),
    });
  } catch {
    /* ignora */
  }
}

type PecaValidacao = Pick<PecaCorte, 'grupoId' | 'codigo' | 'medida' | 'quantidade'>;

/** Valida consumo das peças na chapa; retorna mensagem de erro ou null se OK. */
export function validarPecasContraChapa(
  pecas: PecaValidacao[],
  configuracao: ConfiguracaoChapa
): string | null {
  const extrairTirasConsumo = (pecasGrupo: PecaValidacao[]) => {
    const tiras: Array<{ longMed: number; longQtd: number; transversais: Array<{ med: number; qtd: number }> }> = [];
    let atual: { longMed: number; longQtd: number; transversais: Array<{ med: number; qtd: number }> } | null = null;
    (pecasGrupo || []).forEach((p) => {
      if (p.codigo === 3) {
        atual = {
          longMed: Number(p.medida) || 0,
          longQtd: Math.max(1, Number(p.quantidade) || 1),
          transversais: [],
        };
        tiras.push(atual);
        return;
      }
      if (p.codigo === 4 || p.codigo === 44) {
        if (!atual) return;
        atual.transversais.push({
          med: Number(p.medida) || 0,
          qtd: Math.max(1, Number(p.quantidade) || 1),
        });
      }
    });
    return tiras;
  };

  const porGrupo = new Map<string, PecaValidacao[]>();
  pecas.forEach((p) => {
    if (!porGrupo.has(p.grupoId)) porGrupo.set(p.grupoId, []);
    porGrupo.get(p.grupoId)!.push(p);
  });

  const larguraTotal = Number(configuracao?.larguraTotal) || 2200;
  const comprimentoTotal = Number(configuracao?.comprimentoTotal) || 2750;
  const serra = Number(configuracao?.espessuraSerra) || 4;

  for (const [gid, arr] of porGrupo.entries()) {
    const tiras = extrairTirasConsumo(arr);
    let consumoLongTotal = 0;
    let consumoCompMax = 0;
    for (const t of tiras) {
      if (t.longMed > larguraTotal) {
        return `Erro no grupo ${gid.slice(0, 8)}: peça longitudinal ${t.longMed}mm ultrapassa largura da chapa (${larguraTotal}mm).`;
      }
      let erroTransversal = '';
      const compTira = t.transversais.reduce((s, tr) => {
        if (tr.med > comprimentoTotal) {
          erroTransversal = `Erro no grupo ${gid.slice(0, 8)}: peça transversal ${tr.med}mm ultrapassa comprimento da chapa (${comprimentoTotal}mm).`;
          return s;
        }
        return s + (tr.med + serra) * tr.qtd;
      }, 0);
      if (erroTransversal) return erroTransversal;
      consumoLongTotal += (t.longMed + serra) * t.longQtd;
      consumoCompMax = Math.max(consumoCompMax, compTira);
    }
    if (consumoLongTotal > larguraTotal) {
      return `Erro no grupo ${gid.slice(0, 8)}: consumo longitudinal ${Math.round(consumoLongTotal)}mm ultrapassa largura da chapa (${larguraTotal}mm).`;
    }
    if (consumoCompMax > comprimentoTotal) {
      return `Erro no grupo ${gid.slice(0, 8)}: consumo transversal ${Math.round(consumoCompMax)}mm ultrapassa comprimento da chapa (${comprimentoTotal}mm).`;
    }
  }
  return null;
}

export const getCorCodigo = (codigo: EixoCodigo): string => {
  switch (codigo) {
    case 3: return 'bg-green-100 border-green-300 text-green-800';
    case 5: return 'bg-purple-100 border-purple-300 text-purple-800';
    case 4:
    case 44: return 'bg-blue-100 border-blue-300 text-blue-800';
    default: return 'bg-gray-100 border-gray-300 text-gray-800';
  }
};

export const getEixoNome = (codigo: EixoCodigo): string => {
  if (codigo === 3) return 'LONGITUDINAL';
  if (codigo === 5) return 'LONGITUDINAL (3º CORTE)';
  return 'TRANSVERSAL';
};
